import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { take, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';

import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CompanyBondModel, CompanyModel } from '@data-layer/models/companies';
import { CodeRepository, CompanyBondsRepository } from '@data-layer/repositories';
import { CompaniesService } from '@services/companies.service';
import { enumBondRequestState, enumBondType, enumIdentifierQualifier } from '@data-layer/enums/common';
import { BondModel, BondRequestModel, PortModel, ProductDetailsModel } from '@data-layer/models/common';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { Select, Store } from '@ngxs/store';
import { FetchProductsByCompany, SharedSelectors } from '@app/shared/shared-state';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-entity-bond',
  templateUrl: './entity-bond.component.html',
  styleUrls: ['./entity-bond.component.scss'],
})
export class EntityBondComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public importerIdentifier: string;
  @Input() public importerIdentifierQual: enumIdentifierQualifier;
  @Input() public bondPurchaseAdditionalRemarks: string;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public purchaseBondForm: UntypedFormGroup;
  @Input() public bondRequest: BondRequestModel;
  @Input() public errors: any;
  @Input() public showSuretyCodeForContinuousBond: boolean = false;
  @Input() public showRefNumberForContinuousBond: boolean = false;
  @Input() public referenceNumber: string;
  @Input() public canEdit = true;
  @Input() public isDesktop: boolean = false;
  @Input() public isShowOnlyActive: boolean = false;
  @Input() public isEntrySummary: boolean = false;
  // Customs-broker review (management) mode (#13102): the company-bonds catalog IS fetched, but routed
  // through the injected CompanyBondsRepository adapter (BrokerCompanyBondsRepositoryAdapter — substitutes
  // the reviewed ES owner's companyId, since the active broker company ≠ the importer) and company-bond
  // selection is enabled (the ES company's bonds are valid for its importer). Only product PRICING is
  // skipped — it is keyed on CompaniesService and there is no broker product-price endpoint (pricing UI is
  // already hidden via the `!isManagement` template guards). Default false → user-side unchanged.
  @Input() public isManagement: boolean = false;
  @Output() public updateStepperNextButton: EventEmitter<any> = new EventEmitter<any>();
  @Output() public companyBondsNotFound: EventEmitter<any> = new EventEmitter<any>();
  @Output() public selectCustomContinuousBond: EventEmitter<any> = new EventEmitter<any>();

  @Select(SharedSelectors.getProductsByCompany) public productsByCompany$: Observable<ProductDetailsModel[]>;

  public selectedBondIndex: UntypedFormControl = new UntypedFormControl();
  public companyBonds: CompanyBondModel[];
  public company: CompanyModel;
  public enumBondType: typeof enumBondType = enumBondType;
  public enumBondRequestState: typeof enumBondRequestState = enumBondRequestState;
  public integerInputMap: InputTypeMap = INPUT_TYPES_MAP.INTEGER;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  public defaultErrors = { required: 'FORM_ERROR.REQUIRED' };
  public isWasOpenedPurchaseContactTeem: boolean = false;
  public initPorts: PortModel[] = [];
  public isOpenPriceModal: boolean = false;
  public companyBondDisabled: boolean;

  public continuousBondSuffix: number = 1;
  public purchaseBondSuffix: number = 0;

  public continuousBondPrice: number;
  public singleISFBondPrice: number;
  public singleESBondPrice: number;
  public additionToESBondPrice: number;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private companyBondsRepository: CompanyBondsRepository,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private codeRepository: CodeRepository,
    private cd: ChangeDetectorRef,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    if (!this.isManagement) {
      // Prices come from the active company's products (NGXS) — irrelevant + hidden in broker review.
      this.productsByCompany$.pipe(takeUntil(this.isDestroy)).subscribe((productsByCompany: ProductDetailsModel[]) => {
        this.continuousBondPrice =
          (productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.CONTINUOUS_ANNUAL_BOND)
            ?.product?.fixedPrice || 0;
        this.singleISFBondPrice =
          (productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.ISF_SINGLE_BOND)
            ?.product?.fixedPrice || 0;
        this.singleESBondPrice =
          (productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.ES_SINGLE_BOND)
            ?.product?.fixedPrice || 0;
        this.additionToESBondPrice =
          (productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.ADDITION_TO_ENTRY_BOND)
            ?.product?.fixedPrice || 0;

        this.cd.markForCheck();
      });
    }

    this.loadBonds();

    this.selectedBondIndex.valueChanges.pipe(takeUntil(this.isDestroy)).subscribe((index: number) => {
      this.isWasOpenedPurchaseContactTeem = false;
      this.patchBondByIndex(index);
      this.updateStepperNextButton.emit(this.selectedBondIndex.value === this.userContinuousBondIndex);
    });

    this.formGroup
      ?.get('type')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        if (this.formGroup?.get('bondAmount') && this.companyBonds && this.selectedBondIndex.value === this.companyBonds.length) {
          this.formGroup.get('bondAmount').reset(50000);
        }
      });

    if (this.purchaseBondForm && this.purchaseBondForm.get('portCode') && this.purchaseBondForm.get('portCode').value) {
      this.initPort(this.purchaseBondForm.get('portCode').value);
    }

    if (!this.isManagement) {
      // Product prices are keyed on the active company — irrelevant + unavailable for the broker.
      this.loadContinuesBondProduct();
      this.loadSingleISFBondProduct();
      this.loadSingleESBondProduct();
      this.loadAdditionToEntryBondProduct();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.importerIdentifier || changes.importerIdentifierQual) {
      this.checkCompanyBondsDisabled();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get isSelectedPurchase(): boolean {
    if (!this.companyBonds) {
      return false;
    }
    return this.selectedBondIndex.value === this.bondPurchaseIndex;
  }

  public get isSelectedContinuousBond(): boolean {
    if (!this.companyBonds) {
      return false;
    }
    return this.selectedBondIndex.value === this.bondContinuousIndex;
  }

  public get bondPurchaseIndex(): number {
    return (this.companyBonds || []).length + this.purchaseBondSuffix;
  }

  public get bondContinuousIndex(): number {
    return (this.companyBonds || []).length + this.continuousBondSuffix;
  }

  public get userBondIndex(): number {
    return this.companyBonds?.length || 0;
  }

  public get userContinuousBondIndex(): number {
    return this.companyBonds?.length + this.continuousBondSuffix || this.continuousBondSuffix;
  }

  public openPriceModal(event: Event) {
    event.preventDefault();
    this.isOpenPriceModal = true;
  }

  public closePriceModal() {
    this.isOpenPriceModal = false;
  }

  public onSelectCustomContinuousBond() {
    this.selectCustomContinuousBond.emit();
  }

  public showBondBody(bond: CompanyBondModel, index: number): boolean {
    const isSelected = this.selectedBondIndex.value === index;
    return isSelected && (bond.type === enumBondType.SINGLE_TRANSACTION ? true : this.showSuretyCodeForContinuousBond);
  }

  public bondLabel(bond: CompanyBondModel, index: number): string {
    const isSelected = this.selectedBondIndex.value === index;
    const continuousBondTranslation = this.translateService.instant('BOND_TYPE.CONTINUOUS');
    const singleBondTranslation = this.translateService.instant('BOND_TYPE.SINGLE_TRANSACTION');

    const bondTypeTranslation = bond.type === enumBondType.CONTINUOUS ? continuousBondTranslation : singleBondTranslation;

    if (isSelected) {
      return `${bondTypeTranslation} ${bond.suretyCode}`;
    } else {
      return `${bondTypeTranslation} ${bond.suretyCode} (${bond.referenceNumber})`;
    }
  }

  public getPorts() {
    return this.codeRepository.searchDomesticPorts.bind(this.codeRepository);
  }

  private checkCompanyBondsDisabled() {
    if (this.isManagement) {
      // Broker review: the catalog is the reviewed ES owner's company bonds (valid for its importer), and
      // the active broker company has no comparable identifier to match — enable selection (#13102 / 3d).
      this.companyBondDisabled = false;
      return;
    }
    if (!this.importerIdentifier || !this.importerIdentifierQual) {
      this.companyBondDisabled = true;
      return;
    }
    const company = this.companiesService.getCompany();
    this.companyBondDisabled =
      this.importerIdentifier !== company.identifier || this.importerIdentifierQual !== company.identifierQualifier;
  }

  private initPort(portCode: string) {
    this.codeRepository
      .getDomesticPort(portCode, false)
      .pipe(take(1))
      .subscribe((port) => {
        this.initPorts = [port];
      });
  }

  private loadBonds() {
    this.company = this.companiesService.getCompany();
    // Broker review: the injected CompanyBondsRepository (BrokerCompanyBondsRepositoryAdapter) ignores this
    // id and substitutes the reviewed ES owner's companyId; the active broker company may be absent, so
    // resolve it null-safe. User-side: `this.company` is the active company → identical id as before.
    this.companyBondsRepository
      .get(this.company?.id ?? null)
      .pipe(take(1))
      .subscribe((bonds: CompanyBondModel[]) => this.applyCompanyBonds(bonds));
  }

  private applyCompanyBonds(bonds: CompanyBondModel[]): void {
    this.companyBonds =
      bonds.filter(
        (bond) =>
          [enumBondType.CONTINUOUS, enumBondType.SINGLE_TRANSACTION].includes(bond.type) && (this.isShowOnlyActive ? bond.active : true),
      ) || [];

    this.updateForm();
    this.updateStepperNextButton.emit(this.selectedBondIndex.value === this.userContinuousBondIndex);
    if (this.companyBonds.length === 0) {
      this.companyBondsNotFound.emit();
    }
    this.cd.markForCheck();
  }

  private updateForm() {
    if (!this.formGroup) {
      return;
    }
    const initialBond: BondModel = this.formGroup.value;
    const selectedBondIndex = this.getSelectedBondIndex();

    const selectedBond =
      selectedBondIndex !== this.companyBonds.length && !this.isSelectedPurchase ? this.companyBonds[selectedBondIndex] : null;

    this.selectedBondIndex.patchValue(selectedBondIndex, { emitEvent: false });
    if (selectedBond) {
      this.formGroup.patchValue(selectedBond);
      return;
    }

    if ((selectedBondIndex === this.companyBonds.length || this.isSelectedPurchase) && !initialBond.type) {
      this.formGroup.get('type').patchValue(enumBondType.SINGLE_TRANSACTION);
    }
  }

  private getSelectedBondIndex() {
    if (this.purchaseBondForm && this.bondRequest && this.bondRequest.id && this.bondRequest.bondType === enumBondType.SINGLE_TRANSACTION) {
      return this.bondPurchaseIndex;
    }
    const initialBond: BondModel = this.formGroup.value;
    if (initialBond.type) {
      return this.findBondInDefault(initialBond);
    }
    const companyDefaultBond = this.companiesService.getCompany()?.defaultBondReferenceNumber;
    return this.companyBondDisabled ? this.userBondIndex : this.getCompanyBond(companyDefaultBond);
  }

  private findBondInDefault(bond: BondModel): number {
    const index = this.companyBonds.findIndex(
      (userBond) => userBond.referenceNumber === bond.referenceNumber && userBond.type === bond.type,
    );

    return index === -1 ? (!this.isEntrySummary || !bond.referenceNumber ? this.userContinuousBondIndex : this.userBondIndex) : index;
  }

  private getCompanyBond(companyDefaultBond: string): number {
    const index = this.companyBonds.findIndex((userBond) =>
      companyDefaultBond
        ? userBond.referenceNumber === companyDefaultBond && userBond.type === enumBondType.CONTINUOUS
        : userBond.type === enumBondType.CONTINUOUS,
    );
    return index === -1 ? this.companyBonds.length : index;
  }

  private patchBondByIndex(index: number) {
    if (index === this.companyBonds.length || this.isSelectedPurchase) {
      this.formGroup.patchValue({
        type: enumBondType.SINGLE_TRANSACTION,
        suretyCode: null,
        bondAmount: null,
        referenceNumber: null,
      });
      return;
    }

    if (index === this.companyBonds.length + this.continuousBondSuffix || this.isSelectedContinuousBond) {
      this.formGroup.patchValue({
        type: enumBondType.CONTINUOUS,
        suretyCode: null,
        bondAmount: null,
        referenceNumber: null,
      });
      return;
    }

    if (index < this.companyBonds.length) {
      const bond = this.companyBonds[index];
      const valueToPatch: BondModel = new BondModel({
        type: bond.type,
        referenceNumber: bond.referenceNumber,
      });
      if (bond.type === enumBondType.SINGLE_TRANSACTION) {
        valueToPatch.suretyCode = bond.suretyCode;
      }
      if (bond.type === enumBondType.CONTINUOUS) {
        // #5824
        // if (this.showSuretyCodeForContinuousBond) {
        valueToPatch.suretyCode = bond.suretyCode;
        // }
      }
      if (this.formGroup.get('bondAmount')) {
        valueToPatch.bondAmount = bond.amount;
      }
      this.formGroup.patchValue(valueToPatch);
    }
  }

  private loadContinuesBondProduct(): void {
    this.store.dispatch(
      new FetchProductsByCompany({
        productCode: enumBillItemProductCode.CONTINUOUS_ANNUAL_BOND,
        companyId: this.companiesService.getCompany()?.id,
      }),
    );
  }

  private loadSingleISFBondProduct(): void {
    this.store.dispatch(
      new FetchProductsByCompany({
        productCode: enumBillItemProductCode.ISF_SINGLE_BOND,
        companyId: this.companiesService.getCompany()?.id,
      }),
    );
  }

  private loadSingleESBondProduct(): void {
    this.store.dispatch(
      new FetchProductsByCompany({
        productCode: enumBillItemProductCode.ES_SINGLE_BOND,
        companyId: this.companiesService.getCompany()?.id,
      }),
    );
  }

  private loadAdditionToEntryBondProduct(): void {
    this.store.dispatch(
      new FetchProductsByCompany({
        productCode: enumBillItemProductCode.ADDITION_TO_ENTRY_BOND,
        companyId: this.companiesService.getCompany()?.id,
      }),
    );
  }
}
